'use client';

/**
 * Pluralsight-style outcome grid — 4 cards on a brand-tinted gradient
 * background, each with an icon tile, title, body, and CTA arrow.
 */
import { useTranslation } from 'react-i18next';
import { useBlockPart } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { tx, asList } from '../_shared/coerce';
import { cardReset } from '../_shared/styles';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

/** The `t` returned by {@code useTranslation} — typed structurally so this
 *  package needs no direct dependency on i18next's own types. */
type Translate = (key: string) => string;

interface OutcomeItem {
  title?: Localized;
  body?: Localized;
  ctaLabel?: Localized;
  href?: string;
  icon?: string;
}

export interface PluralsightOutcomeGridProps {
  eyebrow?: Localized;
  title?: Localized;
  subtitle?: Localized;
  items?: OutcomeItem[];
}

/** Built per render so a language switch re-reads the catalogue. */
const buildDefaults = (t: Translate): OutcomeItem[] => [
  {
    icon: 'enterprise',
    title: t('grid.outcomes.enterprise.title'),
    body: t('grid.outcomes.enterprise.body'),
    ctaLabel: t('grid.outcomes.enterprise.cta'),
    href: '/lxp/catalog',
  },
  {
    icon: 'ai',
    title: t('grid.outcomes.ai.title'),
    body: t('grid.outcomes.ai.body'),
    ctaLabel: t('grid.outcomes.ai.cta'),
    href: '/lxp/catalog?topic=ai',
  },
  {
    icon: 'security',
    title: t('grid.outcomes.security.title'),
    body: t('grid.outcomes.security.body'),
    ctaLabel: t('grid.outcomes.security.cta'),
    href: '/lxp/catalog?topic=security',
  },
  {
    icon: 'cloud',
    title: t('grid.outcomes.cloud.title'),
    body: t('grid.outcomes.cloud.body'),
    ctaLabel: t('grid.outcomes.cloud.cta'),
    href: '/lxp/catalog?topic=cloud',
  },
];

function iconFor(icon?: string): string {
  const key = (icon || '').toLowerCase();
  if (key.includes('cloud')) return '☁';
  if (key.includes('security') || key.includes('secure')) return '◆';
  if (key.includes('ai')) return '✦';
  if (key.includes('enterprise')) return '▤';
  return '▣';
}

export function PluralsightOutcomeGrid({
  eyebrow,
  title,
  subtitle,
  items,
}: PluralsightOutcomeGridProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const list = asList<OutcomeItem>(items);
  const outcomes = (list.length ? list : buildDefaults(t)).slice(0, 8);

  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'clamp(56px, 7vw, 88px) 24px',
        background: 'var(--color-surface-page, #fff)',
      }}
    >
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ maxWidth: 760 }}>
          {eyebrow && (
            <div
              style={{
                color: 'var(--color-brand-600, #ec008c)',
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: 'var(--font-tracking-widest, 0.14em)',
                textTransform: 'uppercase',
                marginBottom: 10,
              }}
            >
              {tx(eyebrow)}
            </div>
          )}
          <h2
            {...part('title')}
            style={{
              margin: 0,
              fontSize: 'clamp(34px, 4.5vw, 58px)',
              lineHeight: 1,
              letterSpacing: 'var(--font-tracking-tight, -0.055em)',
              color: 'var(--color-text-primary, #111)',
              fontFamily: 'var(--font-family-display, Inter Tight, system-ui, sans-serif)',
              fontWeight: 900,
            }}
          >
            {tx(title) || t('grid.outcomes.title')}
          </h2>
          {subtitle && (
            <p
              {...part('subtitle')}
              style={{
                margin: '16px 0 0',
                color: 'var(--color-text-secondary, #5b6472)',
                fontSize: 18,
                lineHeight: 1.55,
              }}
            >
              {tx(subtitle)}
            </p>
          )}
        </div>

        <div
          {...part('list')}
          className="ps-outcome-grid"
          style={{
            marginTop: 34,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 18,
          }}
        >
          {outcomes.map((item: OutcomeItem, i: number) => (
            <Card asChild key={i}>
              <a
                {...part('item', i)}
                href={item.href || '#'}
                style={{
                  ...cardReset,
                  minHeight: 300,
                  padding: 22,
                  borderRadius: 18,
                  color: 'var(--color-text-primary, #111)',
                  textDecoration: 'none',
                  background:
                    'linear-gradient(180deg, var(--color-surface-card, #fff), var(--color-neutral-50, #f7f7fb))',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div
                    style={{
                      width: 54,
                      height: 54,
                      borderRadius: 14,
                      display: 'grid',
                      placeItems: 'center',
                      color: '#fff',
                      fontSize: 24,
                      fontWeight: 900,
                      background:
                        i % 2 === 0
                          ? 'linear-gradient(135deg,#ec008c,#ff6b00)'
                          : 'linear-gradient(135deg,#7c3aed,#ec008c)',
                      marginBottom: 22,
                    }}
                  >
                    {iconFor(item.icon)}
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 22,
                      lineHeight: 1.1,
                      letterSpacing: 'var(--font-tracking-tight, -0.03em)',
                    }}
                  >
                    {tx(item.title)}
                  </h3>
                  {item.body && (
                    <p
                      style={{
                        margin: '12px 0 0',
                        color: 'var(--color-text-secondary, #5b6472)',
                        fontSize: 14,
                        lineHeight: 1.55,
                      }}
                    >
                      {tx(item.body)}
                    </p>
                  )}
                </div>
                <div
                  style={{
                    marginTop: 26,
                    color: 'var(--color-brand-600, #d6007f)',
                    fontWeight: 800,
                    fontSize: 14,
                  }}
                >
                  {tx(item.ctaLabel) || t('grid.outcomes.learnMore')} →
                </div>
              </a>
            </Card>
          ))}
        </div>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 1020px) {
          .ps-outcome-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 560px) {
          .ps-outcome-grid { grid-template-columns: 1fr !important; }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
